import moment from "moment-timezone";
import { Fragment, ReactNode, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import Skeleton from "react-loading-skeleton";
import { AnyPeriod, AnyPrice, castPeriod, classNames, TranslatedString, useAppConfig } from "@ct-react/core";
import { useLocaleContext, useLocaleFormatter, useLocaleNavigate, useLocaleUrl } from "@ct-react/locale";
import { BookFullPrice, toGQLBookingPrice } from "@ct-react/calendar";
import { useVisitor } from "@ct-react/visitor";
import {
  AgeCategory,
  BenefitCartOption,
  CartItemOptionInput,
  CartOptionType, CartPrice,
  CustomerContact,
  TouristTaxCartOption
} from "@shared/graphql";
import { buildArticleUrl } from "@shared/url";
import { actions, booking, form, pricing } from "../../i18n/sharable-defs";
import { CheckoutError as CheckoutErrorModel } from "../../models/checkout";
import * as checkoutTools from "../../tools/search-params/checkout";
import { useTools } from "../../hooks/tools";
import { useForm, validators } from "../../hooks/form";
import { useFakingCartItem } from "../../hooks/cart";
import { Wrapper } from "../../components/layout/wrapper";
import Button from "../../components/common/button";
import Select, { Option } from "../../components/inputs/select";
import CheckboxInput from "../../components/inputs/checkbox";
import CheckoutResume from "../../components/checkout/resume";
import CheckoutOptionSelector from "../../components/checkout/options-selector";
import CheckoutPaymentChoice from "../../components/checkout/payment-choice";
import CheckoutError from "../../components/checkout/error";
import bankcardPath from "../../assets/common/card.png";
import invoicePath from "../../assets/common/invoice.png";
import "./checkout.scss";

const transDefs = defineMessages({
  detail: { id: "cart.detail.title", defaultMessage: "Détails du prix" },
  accommodation: { id: "cart.detail.accommodation", defaultMessage: "Logement" },
  resume: { id: "cart.detail.resume", defaultMessage: "Récapitulatif" },
  options: { id: "cart.detail.options-label", defaultMessage: "Services optionnels" },
  tot: { id: "cart.detail.total-label", defaultMessage: "Montant total du loyer" },
  pay: { id: "cart.detail.pay-title", defaultMessage: "Paiement" },
  notes: { id: "cart.detail.notes", defaultMessage: "Notes supplémentaires" },
  customer: { id: "cart.customer", defaultMessage: "Données personnelles" },
  method: { id: "cart.payment-method", defaultMessage: "Modalité de paiement" },
  bankcard: { id: "cart.payment-card", defaultMessage: "Carte bancaire" },
  invoice: { id: "cart.payment-invoice", defaultMessage: "Virement bancaire" },
  invoiceHelp: { id: "cart.payment-invoice-helper", defaultMessage: "Vous recevrez un e-mail avec les modalités de paiement. La location restera pré-réservée 24h dans l’attente de la réception du virement." },
  legal: { id: "cart.pay-legal", defaultMessage: "J'ai lu et accepté les <terms>conditions générales de contrat</terms> et la <policy>politique de confidentialité</policy>." },
  action: { id: "cart.pay-button", defaultMessage: "Confirmer le paiement" },

});

const FOR_CHECKOUT_GQL_DATA = gql`
  query PreCheckout($articleId: ID!, $period: DatePeriod!) {
    detail: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle {
        id slug
        images @limit(max: 1) { assets { url breakpoint } }
        features { maxGuests }
        costDescriptions { bookingNotes { value } }
        touristTax { optionId grid { ageCategory price } }
      }
    }
    price: bookingAccommodationPriceDetail(articleId: $articleId period: $period) {
      bookable
      price {
        amount
        detailed { bare discount providerCharge { adaptive amount } }
      }
      applicableDownPayment
    }
    barePrice: bookingAccommodationBarePriceDetail(articleId: $articleId period: $period) {
      label { value }
      amount
    }
    options: cartCheckOptionsBeforeAddItem(
      item: { rentalAccommodation: { articleId: $articleId period: $period } }
      type: BENEFIT
    ) {
      ... on BenefitCartOption {
        optionId
        title { value } description { value }
        criteria { quantity }
        grid { quantity price }
      }
    }
  }
`;

const CHECK_AVAILABILITY_GQL_DATA = gql`
  query CheckoutCheck($articleId: ID!, $period: DatePeriod!) {
    available: bookingAccommodationPriceDetail(articleId: $articleId period: $period) { price { amount } }
  }
`;

const ADD_CART_GQL_MUT = gql`
  mutation AddToCart($sessionId: String, $userId: String, $item: AnyAddCartItemInput!, $options: [AnyAddCartItemOptionInput!]) {
    cartAddItem(sessionId: $sessionId, userId: $userId, item: $item, options: $options) { cartId }
  }
`;

const CHECKOUT_GQL_MUT = gql`
  mutation Checkout($sessionId: String, $userId: String, $checkout: CheckoutCartInput!) {
    cartCheckout(sessionId: $sessionId, userId: $userId, checkout: $checkout) @recaptcha(action: "checkout") { paymentUrl }
  }
`;

type FormData = {
  customer: CustomerContact;
  legal: boolean;
}

type FormProps = {
  articleId: string;
  period: AnyPeriod;
  barePrice: { label: TranslatedString, amount: AnyPrice }[];
  price: BookFullPrice;
  textComplement?: TranslatedString;
  guests?: Record<AgeCategory, number>;
  downPaymentRate?: number;
  touristTaxConfig?: TouristTaxCartOption;
  optionsConfig: BenefitCartOption[];
  onAmountChange: (amount: CartPrice) => void;
  onError: (error: CheckoutErrorModel) => void;
}

const CheckoutForm = (
  {
    articleId,
    period,
    price,
    barePrice,
    textComplement,
    guests,
    downPaymentRate,
    touristTaxConfig,
    optionsConfig,
    onAmountChange,
    onError
  }: FormProps) => {

  const intl = useIntl();
  const { baseUrl } = useAppConfig();
  const { sessionId, userId } = useVisitor();
  const { locale } = useLocaleContext();
  const { buildLocaleHref } = useLocaleUrl();
  const navigate = useLocaleNavigate();
  const { print } = useLocaleFormatter();
  const { countries, format, mutation } = useTools();

  const termsPath = useMemo(() => `/conditions/policy-${locale.basename.toLowerCase()}.pdf`, [ locale ]);
  const policyPath = useMemo(() => `/conditions/policy-${locale.basename.toLowerCase()}.pdf`, [ locale ]);

  const { register, formState, patch, handleSubmit } = useForm<FormData>({ customer: { address: { country: "CH" } } });
  const [ options, setOptions ] = useState<CartItemOptionInput[]>([]);
  const { taxes, fakeCartItem } = useFakingCartItem(price, period, options, guests, touristTaxConfig, downPaymentRate);
  const [ withInstantPayment, setWithInstantPayment ] = useState<boolean>(true);
  const [ hasFullPayment, setHasFullPayment ] = useState<boolean>(true);

  const invoiceable = useMemo(() => castPeriod(period).start.diff(moment(), "days") >= 9, [ period ]);

  useEffect(() => {
    onAmountChange(fakeCartItem.price);
    patch(true);
  }, [ fakeCartItem ]);

  useEffect(() => {
    (!withInstantPayment && !invoiceable) && setWithInstantPayment(true);
  }, [ invoiceable ]);

  const [ checkAvailability ] = useLazyQuery<{ available: any }>(CHECK_AVAILABILITY_GQL_DATA, { fetchPolicy: "no-cache" });
  const [ addToCart ] = useMutation(ADD_CART_GQL_MUT);
  const [ checkout ] = useMutation<{ cartCheckout: any }>(CHECKOUT_GQL_MUT);

  const fullCheck = () => new Promise<any>(async (resolve, reject) => {
    const variables = { articleId, period };
    const { data } = await checkAvailability({ variables });
    if (!data?.available) {
      reject();
      return;
    }
    const formatedPrice = toGQLBookingPrice(price.detailed);
    !!formatedPrice.providerCharge && delete (formatedPrice.providerCharge as any).__typename;
    resolve({
      sessionId,
      userId,
      item: { rentalAccommodation: { articleId, period, price: formatedPrice } },
      ...(!!taxes || options.length > 0) && {
        options: [ ...(!!taxes ? [ { touristTax: taxes } ] : []), ...options.map(benefit => ({ benefit })) ]
      }
    });
  });

  const onPay = ({ customer }: FormData) => fullCheck()
    .catch(() => Promise.reject("unavailable"))
    .then(variables => addToCart({ variables }).catch(() => Promise.reject("unknown")))
    .then(() => ({
      sessionId,
      userId,
      checkout: {
        customer,
        hasFullPayment,
        withInstantPayment,
        redirect: {
          baseUrl,
          successPath: buildLocaleHref("/checkout/success/"),
          cancelPath: buildLocaleHref("/checkout/cancellation/")
        }
      }
    }))
    .then(variables => mutation.withRecaptcha(checkout, "checkout")({ variables }))
    .then(({ data }) => !data?.cartCheckout.paymentUrl
      ? navigate("/checkout/success/?no-payment", { replace: true })
      : window.location.replace(data.cartCheckout.paymentUrl))
    .catch(onError);

  return (
    <form onSubmit={handleSubmit(onPay)}>

      <div className="group">
        <h2>{intl.formatMessage(transDefs.detail)}</h2>

        <div>
          <h3>{intl.formatMessage(transDefs.accommodation)}</h3>
          {barePrice.length === 0 &&
            <dl>
              <dt>{`${intl.formatMessage(transDefs.accommodation)} - ${intl.formatMessage(booking.nights, { count: format.nights(period) })}`}</dt>
              <dd>{print.price(price.detailed.bare)}</dd>
            </dl>
          }
          {barePrice.length > 0 &&
            <dl>
              {barePrice.map((item, index) => (
                <Fragment key={index}>
                  <dt>{item.label.value}</dt>
                  <dd>{print.price(item.amount)}</dd>
                </Fragment>)
              )}
            </dl>
          }
        </div>

        {optionsConfig.length > 0 &&
          <div className="separator">
            <h3>{intl.formatMessage(transDefs.options)}</h3>
            <CheckoutOptionSelector config={optionsConfig} onSelection={setOptions} />
          </div>
        }

        <div className="separator">
          <h3>{intl.formatMessage(transDefs.resume)}</h3>
          <dl>
            <dt>{intl.formatMessage(transDefs.tot)}</dt>
            <dd>{print.price(fakeCartItem.price.bare)}</dd>
            {!!fakeCartItem.price.discount &&
              <>
                <dt>{intl.formatMessage(pricing.discount)}</dt>
                <dd className="discount">{print.price(fakeCartItem.price.discount)}</dd>
              </>
            }
            {(() => {
              const benefits = fakeCartItem.price.options.find(opt => opt.type === CartOptionType.BENEFIT);
              const taxes = fakeCartItem.price.options.find(opt => opt.type === CartOptionType.TOURIST_TAX);
              return (
                <>
                  {!!benefits &&
                    <>
                      <dt>{intl.formatMessage(transDefs.options)}</dt>
                      <dd>{print.price(benefits.amount)}</dd>
                    </>
                  }
                  {!!taxes &&
                    <>
                      <dt>{intl.formatMessage(pricing.taxes)}</dt>
                      <dd>{print.price(taxes.amount)}</dd>
                    </>
                  }
                </>);
            })()}
            <dt className="tot">{intl.formatMessage(pricing.total)}</dt>
            <dd className="tot">{print.price(fakeCartItem.price.amount)}</dd>
          </dl>
        </div>

        <div className="separator">
          <h3>{intl.formatMessage(transDefs.pay)}</h3>
          <CheckoutPaymentChoice item={fakeCartItem} onChoice={setHasFullPayment} />
        </div>

      </div>

      {!!textComplement &&
        <div className="group">
          <h2>{intl.formatMessage(transDefs.notes)}</h2>
          <div className="html-format" dangerouslySetInnerHTML={{ __html: textComplement.value }} />
        </div>
      }

      <div className="group">
        <h2>{intl.formatMessage(transDefs.customer)}</h2>
        <div className="inner customer-form">

          <div className="f-group">
            <label htmlFor="firstName">{`${intl.formatMessage(form.firstName)}*`}</label>
            <input type="text" id="firstName"
                   className={classNames({ dirty: "customer.firstName" in formState.dirtyFields })}
                   {...register("customer.firstName", { required: intl.formatMessage(form.emptyError) })}
                   placeholder={intl.formatMessage(form.firstName)}
                   autoComplete="given-name" />
            <p className="f-error">
              {!!formState.dirtyFields["customer.firstName"] && formState.dirtyFields["customer.firstName"].error}
            </p>
          </div>

          <div className="f-group">
            <label htmlFor="lastName">{`${intl.formatMessage(form.lastName)}*`}</label>
            <input type="text" id="lastName"
                   className={classNames({ dirty: "customer.lastName" in formState.dirtyFields })}
                   {...register("customer.lastName", { required: intl.formatMessage(form.emptyError) })}
                   placeholder={intl.formatMessage(form.lastName)}
                   autoComplete="family-name" />
            <p className="f-error">
              {!!formState.dirtyFields["customer.lastName"] && formState.dirtyFields["customer.lastName"].error}
            </p>
          </div>

          <div className="f-group">
            <label htmlFor="email">{`${intl.formatMessage(form.email)}*`}</label>
            <input type="email" id="email"
                   className={classNames({ dirty: "customer.email" in formState.dirtyFields })}
                   {...register("customer.email", {
                     required: intl.formatMessage(form.emptyError),
                     validators: { ...validators.emailFormat(intl.formatMessage(form.emailError)) }
                   })}
                   placeholder={intl.formatMessage(form.email)}
                   autoComplete="email" />
            <p className="f-error">
              {!!formState.dirtyFields["customer.email"] && formState.dirtyFields["customer.email"].error}
            </p>
          </div>

          <div className="f-group">
            <label htmlFor="phone">{`${intl.formatMessage(form.phone)}*`}</label>
            <input type="tel" id="phone"
                   className={classNames({ dirty: "customer.phone" in formState.dirtyFields })}
                   {...register("customer.phone", {
                     required: intl.formatMessage(form.emptyError),
                     validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError)) }
                   })}
                   placeholder={intl.formatMessage(form.phone)}
                   autoComplete="tel" />
            <p className="f-error">
              {!!formState.dirtyFields["customer.phone"] && formState.dirtyFields["customer.phone"].error}
            </p>
          </div>

          <div className="f-group">
            <label htmlFor="address">{`${intl.formatMessage(form.address)}*`}</label>
            <div className="fl-group">
              <input type="text" id="address"
                     className={classNames({ dirty: "customer.address.street" in formState.dirtyFields })}
                     {...register("customer.address.street", {
                       required: intl.formatMessage(form.emptyError)
                     })}
                     placeholder={intl.formatMessage(form.street)}
                     autoComplete="street-address" />
              <input type="text"
                     className={classNames("street-min", { dirty: "customer.address.streetNumber" in formState.dirtyFields })}
                     {...register("customer.address.streetNumber")}
                     placeholder={intl.formatMessage(form.streetNumber)} />
            </div>
            <p className="f-error">
              {!!formState.dirtyFields["customer.address.street"] && formState.dirtyFields["customer.address.street"].error}
            </p>
          </div>

          <div className="f-group no-error">
            <label htmlFor="address-complement">{intl.formatMessage(form.streetComplement)}</label>
            <input type="text" id="address-complement"
                   className={classNames({ dirty: "customer.address.streetComplement" in formState.dirtyFields })}
                   {...register("customer.address.streetComplement")}
                   placeholder={intl.formatMessage(form.streetComplement)} />
          </div>

          <div className="f-group">
            <label htmlFor="city">{`${intl.formatMessage(form.city)}*`}</label>
            <div className="fl-group">
              <input type="text"
                     className={classNames("zip-min", { dirty: "customer.address.zip" in formState.dirtyFields })}
                     {...register("customer.address.zip", {
                       required: intl.formatMessage(form.emptyError)
                     })}
                     placeholder={intl.formatMessage(form.zip)}
                     autoComplete="postal-code" />
              <input type="text" id="city"
                     className={classNames({ dirty: "customer.address.city" in formState.dirtyFields })}
                     {...register("customer.address.city", {
                       required: intl.formatMessage(form.emptyError)
                     })}
                     placeholder={intl.formatMessage(form.city)} />
            </div>
            <p className="f-error">
              {(!!formState.dirtyFields["customer.address.zip"] || !!formState.dirtyFields["customer.address.city"]) &&
                (formState.dirtyFields["customer.address.zip"] || formState.dirtyFields["customer.address.city"]).error
              }
            </p>
          </div>

          <div className="f-group">
            <label>{`${intl.formatMessage(form.country)}*`}</label>
            <Select className={classNames({ dirty: "customer.address.country" in formState.dirtyFields })}
                    placeholder={intl.formatMessage(form.countryPlaceholder)}
                    {...register("customer.address.country", {
                      required: intl.formatMessage(form.emptyError)
                    })}>
              {Object.entries(countries).map(([ value, label ], index) => (
                <Option key={index} value={value} label={label} />
              ))}
            </Select>
            <p className="f-error">
              {!!formState.dirtyFields["customer.address.country"] && formState.dirtyFields["customer.address.country"].error}
            </p>
          </div>
        </div>
      </div>

      <div className="group method">
        <h2>{intl.formatMessage(transDefs.method)}</h2>
        <div className="inner">

          <div className="payment-terms">
            <label className="method-choice">
              <input type="radio" name="choice"
                     id="card" value="card"
                     checked={withInstantPayment}
                     onChange={() => setWithInstantPayment(true)} />
              <img src={bankcardPath} alt={intl.formatMessage(transDefs.bankcard)} />
              {intl.formatMessage(transDefs.bankcard)}
            </label>

            {invoiceable &&
              <label className="method-choice">
                <input type="radio" name="choice"
                       id="invoice" value="invoice"
                       checked={!withInstantPayment}
                       onChange={() => setWithInstantPayment(false)} />
                <img src={invoicePath} alt={intl.formatMessage(transDefs.invoice)} />
                {intl.formatMessage(transDefs.invoice)}
              </label>
            }
          </div>

          {!withInstantPayment &&
            <div className="f-group">
              {intl.formatMessage(transDefs.invoiceHelp)}
            </div>
          }

          <div className="f-group">
            {/*@ts-ignore*/}
            <CheckboxInput id="legal"
                           label={intl.formatMessage(transDefs.legal, {
                             terms: (chunks: ReactNode) => (<a key="terms" href={termsPath} target="_blank">{chunks}</a>),
                             policy: (chunks: ReactNode) => (<a key="policy" href={policyPath} target="_blank">{chunks}</a>)
                           })}
                           {...register<boolean>("legal", { required: intl.formatMessage(form.emailError)})} />
          </div>

          <Button type="submit" className="bold with-icon"
                  processing={formState.submit.inProcess}
                  disabled={!formState.isValid}>
            {intl.formatMessage(withInstantPayment ? transDefs.action : actions.book)}
            <IconChevronRight />
          </Button>

        </div>
      </div>

    </form>);
}

const CheckoutPage = () => {

  const intl = useIntl();
  const navigate = useLocaleNavigate();

  const [ searchParams, setSearchParams ] = useSearchParams();
  const { articleId, period, guests } = useMemo(() => checkoutTools.parse(searchParams), [ searchParams ]);

  const [ failure, setFailure ] = useState<CheckoutErrorModel>();
  const [ finalPrice, setFinalPrice ] = useState<CartPrice>();

  const { data, loading } = useQuery<any>(FOR_CHECKOUT_GQL_DATA, {
    ssr: false,
    variables: { articleId, period }
  });

  useEffect(() => {
    if (!data) return;
    const validGuests = !data?.detail.features.maxGuests ||
      data.detail.features.maxGuests >= Object.values(guests).reduce((acc, cur) => acc + cur, 0);
    if (!!data.price && data.price.bookable && validGuests) return;
    setFailure(!validGuests ? "overcrowded" : "unavailable");
  }, [ data ]);

  useEffect(() => {
    const check = failure === "overcrowded" && !!data?.detail.features.maxGuests &&
      Object.values(guests).reduce((acc, cur) => acc + cur, 0) <= data.detail.features.maxGuests;
    check && setFailure(undefined);
  }, [ guests ]);

  const onGuestsChange = (guests?: Record<AgeCategory, number>) => {
    checkoutTools.rebuildGuestsOnly(guests, searchParams);
    setSearchParams(searchParams, { replace: true });
  }

  if (loading || !data)
    return (
      <div className="checkout">
        <meta name="robots" content="noindex" />
        <Wrapper.Centered className="content">
          <aside><CheckoutResume loading={true} /></aside>
          <main className="steps">
            <div className="group">
              <h2><Skeleton width="15rem" /></h2>
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i}
                     className={classNames({ separator: i > 0 })}>
                  <h3><Skeleton width="6rem" /></h3>
                  {Array.from({ length: 3}).map((_, j) => (
                    <dl key={j}>
                      <dt><Skeleton width="10rem" /></dt>
                      <dd><Skeleton width="5rem" /></dd>
                    </dl>)
                  )}
                </div>)
              )}
              <div className="separator">
                <h3><Skeleton width="8rem" /></h3>
                {Array.from({ length: 2}).map((_,i) => (
                  <dl key={i}>
                    <dt><Skeleton width={`${(i+1)*6}rem`} /></dt>
                    <dd><Skeleton width="5rem" /></dd>
                  </dl>)
                )}
              </div>
            </div>
            <div className="group">
              <h2><Skeleton width="23rem" /></h2>
              <Skeleton width="20rem" />
              <Skeleton width="100%" count={3} />
              <Skeleton width="12rem" />
            </div>
          </main>
        </Wrapper.Centered>
      </div>);

  return (
    <div className="checkout">
      <meta name="robots" content="noindex" />
      <Wrapper.Centered className="content">

        <aside>
          <CheckoutResume loading={false}
                          data={{
                            image: data!.detail.images[0],
                            minGuests: { [AgeCategory.ADULT]: 1 },
                            maxGuests: data.detail.features.maxGuests,
                            guestable: !!data!.detail.touristTax,
                            period,
                            guests,
                            price: finalPrice
                          }}
                          onGuestsChange={onGuestsChange} />
          <Button type="button" className="outline with-icon"
                  onClick={() => navigate(buildArticleUrl(data.detail), { replace: true })}>
            <IconChevronLeft />
            {intl.formatMessage(actions.back)}
          </Button>
        </aside>

        <main className="steps">
          {(!data.price || failure)
            ? <CheckoutError maxGuests={data.detail.features.maxGuests}
                             error={failure || "unavailable"} />
            : <CheckoutForm {...{ articleId, period, price: data.price.price as BookFullPrice, barePrice: data.barePrice, guests }}
                            textComplement={data.detail.costDescriptions.bookingNotes}
                            downPaymentRate={data.price.applicableDownPayment}
                            touristTaxConfig={data.detail.touristTax}
                            optionsConfig={data.options || []}
                            onAmountChange={setFinalPrice}
                            onError={setFailure} />
          }
        </main>

      </Wrapper.Centered>
    </div>);

};

export default CheckoutPage;
